import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowUpRight, Compass } from "lucide-react";
import { geoMercator, geoPath } from "d3-geo";
import { PLACES } from "./data";
import { useContent } from "./useContent";

/* -----------------------------------------------------------
   Geography — flat 2D map · India + Cambodia only
------------------------------------------------------------ */

const GOLD = "#e6c769";
const GOLD_SOFT = "#fcd28a";
const CAMBODIA = "#7dd3fc";

const VIEW_W = 900;
const VIEW_H = 640;

/* Build a smooth arc path between two projected screen coords (quadratic bezier lifted above) */
const buildArc = (a, b) => {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const dist = Math.hypot(dx, dy);
  const lift = Math.min(160, dist * 0.35);
  const mx = (a[0] + b[0]) / 2;
  const my = (a[1] + b[1]) / 2 - lift;
  return `M ${a[0]} ${a[1]} Q ${mx} ${my} ${b[0]} ${b[1]}`;
};

const Geography = () => {
  const places = useContent("places", PLACES);
  const [active, setActive] = React.useState(places[0]?.id || PLACES[0].id);
  const [hover, setHover] = React.useState(null);
  const [countries, setCountries] = React.useState([]);
  const activePlace = places.find((p) => p.id === active) || places[0] || PLACES[0];

  React.useEffect(() => {
    if (places.length && !places.find((p) => p.id === active)) {
      setActive(places[0].id);
    }
  }, [places, active]);

  // Fetch India + Cambodia geojson polygons
  React.useEffect(() => {
    let cancelled = false;
    fetch("https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson")
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const feats = data.features.filter(
          (f) => f.properties.ADMIN === "India" || f.properties.ADMIN === "Cambodia"
        );
        setCountries(feats);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Build projection that fits both India + Cambodia snugly
  const { pathFn, projectFn } = React.useMemo(() => {
    // Bounding box: India (68°E–98°E, 6°N–37°N) + Cambodia (extends to ~108°E)
    const projection = geoMercator()
      .center([88, 21])
      .scale(720)
      .translate([VIEW_W / 2, VIEW_H / 2]);
    return { pathFn: geoPath(projection), projectFn: projection };
  }, []);

  // Project each place to screen coords
  const projectedPlaces = React.useMemo(
    () =>
      places.map((p) => {
        const [x, y] = projectFn([p.lng, p.lat]) || [0, 0];
        return { ...p, x, y };
      }),
    [projectFn, places]
  );

  // Arcs — link consecutive places along the journey
  const arcs = React.useMemo(() => {
    const out = [];
    for (let i = 0; i < projectedPlaces.length - 1; i++) {
      const a = projectedPlaces[i];
      const b = projectedPlaces[i + 1];
      out.push({
        id: `${a.id}-${b.id}`,
        d: buildArc([a.x, a.y], [b.x, b.y]),
        color: b.country === "Cambodia" ? CAMBODIA : GOLD,
      });
    }
    return out;
  }, [projectedPlaces]);

  return (
    <section id="geography" className="relative py-28 px-6 md:px-10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">
            Ahmedabad · Bengaluru · Sonipat · Siem Reap
          </div>
          <div className="mt-3 grid lg:grid-cols-2 gap-10 items-end">
            <h2
              className="text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              A journey across <span className="italic">two countries</span>.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-md justify-self-end">
              Every city taught me something the last one couldn&rsquo;t. From the streets of Ahmedabad to a village school in Siem Reap — the two lands that shaped how I see the world.
            </p>
          </div>
        </motion.div>

        {/* MAP + Detail */}
        <div className="grid lg:grid-cols-[1.35fr_1fr] gap-8 items-start">
          {/* MAP */}
          <div
            className="relative rounded-[28px] border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur-xl overflow-hidden"
            data-testid="geography-map-wrapper"
          >
            {/* Ambient glow */}
            <div
              className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-40"
              style={{ background: `${GOLD}22` }}
            />
            <div
              className="pointer-events-none absolute -bottom-28 -left-24 w-[360px] h-[360px] rounded-full blur-3xl opacity-30"
              style={{ background: `${CAMBODIA}22` }}
            />

            {/* Chrome */}
            <div className="relative z-10 flex items-center justify-between px-6 pt-6">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase font-mono text-white/50">
                <Compass className="w-3.5 h-3.5 text-[#e6c769]" />
                India · Cambodia
              </div>
              <div className="flex items-center gap-4 text-[10px] tracking-[0.25em] uppercase font-mono">
                <span className="flex items-center gap-1.5 text-white/55">
                  <span className="w-2 h-2 rounded-full" style={{ background: GOLD }} />
                  India
                </span>
                <span className="flex items-center gap-1.5 text-white/55">
                  <span className="w-2 h-2 rounded-full" style={{ background: CAMBODIA }} />
                  Cambodia
                </span>
              </div>
            </div>

            <div className="relative p-4 md:p-6">
              <svg
                viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                className="w-full h-auto"
                style={{ minHeight: 480 }}
              >
                <defs>
                  <linearGradient id="indiaFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GOLD_SOFT} stopOpacity="0.35" />
                    <stop offset="100%" stopColor={GOLD} stopOpacity="0.18" />
                  </linearGradient>
                  <linearGradient id="cambodiaFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CAMBODIA} stopOpacity="0.4" />
                    <stop offset="100%" stopColor={CAMBODIA} stopOpacity="0.18" />
                  </linearGradient>
                  <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* Grid backdrop */}
                <g opacity="0.08">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <line
                      key={`v${i}`}
                      x1={(i * VIEW_W) / 20}
                      y1={0}
                      x2={(i * VIEW_W) / 20}
                      y2={VIEW_H}
                      stroke="#e6c769"
                      strokeWidth="0.4"
                    />
                  ))}
                  {Array.from({ length: 14 }).map((_, i) => (
                    <line
                      key={`h${i}`}
                      x1={0}
                      y1={(i * VIEW_H) / 14}
                      x2={VIEW_W}
                      y2={(i * VIEW_H) / 14}
                      stroke="#e6c769"
                      strokeWidth="0.4"
                    />
                  ))}
                </g>

                {/* Country polygons */}
                {countries.map((f, i) => {
                  const d = pathFn(f);
                  const isCambodia = f.properties.ADMIN === "Cambodia";
                  return (
                    <g key={i}>
                      {/* Outer glow */}
                      <motion.path
                        d={d}
                        fill="none"
                        stroke={isCambodia ? CAMBODIA : GOLD}
                        strokeWidth="6"
                        opacity="0.15"
                        initial={{ pathLength: 0 }}
                        whileInView={{ pathLength: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
                      />
                      {/* Fill */}
                      <motion.path
                        d={d}
                        fill={isCambodia ? "url(#cambodiaFill)" : "url(#indiaFill)"}
                        stroke={isCambodia ? CAMBODIA : GOLD_SOFT}
                        strokeWidth="1.2"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.4, delay: 0.2 }}
                      />
                    </g>
                  );
                })}

                {/* Arcs */}
                {arcs.map((arc, i) => (
                  <motion.path
                    key={arc.id}
                    d={arc.d}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeDasharray="3 6"
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 0.85 }}
                    viewport={{ once: true }}
                    transition={{ duration: 2.2, delay: 0.6 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                    filter="url(#glow)"
                  />
                ))}

                {/* Place markers */}
                {projectedPlaces.map((p, i) => {
                  const isActive = p.id === active;
                  const isHover = p.id === hover;
                  const color = p.country === "Cambodia" ? CAMBODIA : GOLD;
                  return (
                    <motion.g
                      key={p.id}
                      transform={`translate(${p.x}, ${p.y})`}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.9 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                      style={{ cursor: "pointer" }}
                      onClick={() => setActive(p.id)}
                      onMouseEnter={() => setHover(p.id)}
                      onMouseLeave={() => setHover(null)}
                    >
                      {/* Pulsing halo */}
                      {isActive && (
                        <>
                          <motion.circle
                            r="10"
                            fill="none"
                            stroke={color}
                            strokeWidth="1"
                            initial={{ r: 10, opacity: 0.7 }}
                            animate={{ r: [10, 26], opacity: [0.7, 0] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
                          />
                          <circle r="14" fill={color} opacity="0.18" />
                        </>
                      )}
                      {/* Dot */}
                      <circle
                        r={isActive ? 6.5 : isHover ? 5.5 : 4.5}
                        fill={color}
                        stroke="#06070b"
                        strokeWidth="2"
                        filter="url(#glow)"
                      />
                      {/* Label */}
                      <g
                        transform="translate(10, -10)"
                        style={{
                          opacity: isActive || isHover ? 1 : 0.75,
                          transition: "opacity 200ms",
                        }}
                      >
                        <rect
                          x="0"
                          y="-11"
                          rx="10"
                          height="20"
                          width={p.name.length * 6.5 + 18}
                          fill="rgba(6,7,11,0.85)"
                          stroke={color}
                          strokeOpacity="0.4"
                        />
                        <text
                          x={p.name.length * 3.25 + 9}
                          y="3"
                          textAnchor="middle"
                          fontSize="10"
                          fontFamily="'JetBrains Mono', monospace"
                          fontWeight="500"
                          fill={color}
                          style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}
                        >
                          {p.name}
                        </text>
                      </g>
                    </motion.g>
                  );
                })}
              </svg>
            </div>

            <div className="relative z-10 px-6 pb-5 flex items-center justify-between text-[10px] tracking-[0.28em] uppercase font-mono text-white/40">
              <span>Click a marker · Explore each stop</span>
              <span>{places.length} stops · 2 nations</span>
            </div>
          </div>

          {/* SIDE PANEL */}
          <div className="relative">
            <div className="mb-4 flex flex-wrap gap-1.5">
              {places.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setActive(p.id)}
                  data-testid={`geography-marker-${p.id}`}
                  className={`text-[10.5px] tracking-[0.2em] uppercase font-mono px-3 py-1.5 rounded-full border transition-all ${
                    p.id === active ? "text-black" : "text-white/55 hover:text-white border-white/10 hover:border-white/25"
                  }`}
                  style={
                    p.id === active
                      ? {
                          background: p.country === "Cambodia" ? CAMBODIA : GOLD,
                          borderColor: p.country === "Cambodia" ? CAMBODIA : GOLD,
                        }
                      : undefined
                  }
                >
                  {p.name}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activePlace.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="relative rounded-[24px] border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-xl p-8 md:p-10 overflow-hidden"
              >
                <div
                  className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl"
                  style={{ background: activePlace.country === "Cambodia" ? `${CAMBODIA}22` : `${GOLD}22` }}
                />
                <div
                  className="text-[10px] tracking-[0.4em] uppercase font-mono"
                  style={{ color: activePlace.country === "Cambodia" ? CAMBODIA : GOLD }}
                >
                  {activePlace.role} · {activePlace.year}
                </div>
                <h3
                  className="mt-3 text-[30px] md:text-[42px] leading-[1.05] tracking-tight text-white"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {activePlace.name}
                </h3>
                <div className="mt-2 text-[13px] text-white/55 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  {activePlace.country}
                  <span className="text-white/25 mx-1">·</span>
                  <span className="font-mono">
                    {activePlace.lat.toFixed(2)}°N, {activePlace.lng.toFixed(2)}°E
                  </span>
                </div>

                <div className="mt-5 h-px bg-white/10" />
                <div className="mt-5 text-[11px] tracking-[0.3em] uppercase text-white/40 font-mono">Phase</div>
                <div className="mt-1 text-[15px] text-white/85">{activePlace.phase}</div>
                <p className="mt-6 text-[15px] leading-[1.75] text-white/70">{activePlace.note}</p>
                <div className="mt-8 flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-white/40 font-mono">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                  Explore each stop
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Geography;
